import { CSSProperties, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  CheckCircleOutlined,
  LeftOutlined,
  LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ApplicationSummary from '@/components/ApplicationSummary';
import { useGetPurchaseApplicationSummaryQuery } from '@/pages/mortgage/apis/purchaseAPI';
import { useGetFactfindListQuery } from '@/pages/mortgage/apis/factfindAPI';
import FactFindSections from './FactFind/FactFindSections';
import { FactFindFormType } from './FactFind/schema';

const tabStyle = (active: boolean, disabled: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 44,
  padding: '0 8px',
  marginBottom: -1,
  border: 0,
  borderBottom: `3px solid ${active ? '#f5bf36' : 'transparent'}`,
  background: 'transparent',
  font: 'inherit',
  fontSize: 16,
  fontWeight: 500,
  color: disabled ? '#94a3b8' : active ? '#1e293b' : '#64748b',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const PurchaseApplicationSummary = () => {
  const params = useParams();
  // ?tab=factfind (from the applications list) opens on the Fact Find tab;
  // it still only shows once the fact-find is submitted.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<'purchase' | 'factfind'>(
    searchParams.get('tab') === 'factfind' ? 'factfind' : 'purchase',
  );
  const { data, isLoading } = useGetPurchaseApplicationSummaryQuery({
    application_id: params.id as string,
  });
  const { data: factfinds } = useGetFactfindListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const factfind = factfinds?.find(
    (f) => f.assessmentType === 'purchase' && f.assessmentId === params.id,
  );
  const ffLocked = factfind?.data?.status !== 'submitted';
  const showFactFind = tab === 'factfind' && !ffLocked;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Link
          to="/mortgage/purchase"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            width: 'fit-content',
            minHeight: 32,
            fontSize: 14,
            color: '#003862',
            textDecoration: 'none',
          }}
        >
          <LeftOutlined style={{ fontSize: 12 }} />
          Back to Purchase Applications
        </Link>
        <h1
          style={{ margin: 0, fontSize: 22, fontWeight: 500, color: '#1e293b' }}
        >
          Purchase Application Summary
        </h1>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px 24px',
          flexWrap: 'wrap',
          borderBottom: '1px solid #e1e1e1',
        }}
      >
        <div
          role="tablist"
          aria-label="Summary"
          style={{ display: 'flex', gap: 8 }}
        >
          <button
            type="button"
            role="tab"
            aria-selected={!showFactFind}
            onClick={() => setTab('purchase')}
            style={tabStyle(!showFactFind, false)}
          >
            Purchase Application
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showFactFind}
            aria-disabled={ffLocked}
            aria-describedby={ffLocked ? 'ff-lock-note' : undefined}
            onClick={() => !ffLocked && setTab('factfind')}
            style={tabStyle(showFactFind, ffLocked)}
          >
            {ffLocked && <LockOutlined aria-hidden />}
            Fact Find
          </button>
        </div>
        {ffLocked && (
          <span id="ff-lock-note" style={{ fontSize: 13, color: '#64748b' }}>
            Available once your fact-find has been submitted.
          </span>
        )}
      </div>

      {showFactFind && factfind ? (
        <div
          role="tabpanel"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            // match ApplicationSummary's trailing space (card margin + wrapper padding)
            paddingBottom: 36,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              color: '#15803d',
            }}
          >
            <CheckCircleOutlined aria-hidden />
            Fact-find submitted on{' '}
            {dayjs(factfind.updatedAt).format('D MMMM YYYY')}
          </div>
          <FactFindSections
            values={factfind.data as FactFindFormType}
            variant="cards"
          />
        </div>
      ) : (
        <div role="tabpanel">
          <ApplicationSummary data={data} isLoading={isLoading} />
        </div>
      )}
    </div>
  );
};

export default PurchaseApplicationSummary;
